import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeviceThermostatRoundedIcon from '@mui/icons-material/DeviceThermostatRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import PublishedWithChangesRoundedIcon from '@mui/icons-material/PublishedWithChangesRounded';
import SettingsInputAntennaRoundedIcon from '@mui/icons-material/SettingsInputAntennaRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CableRoundedIcon from '@mui/icons-material/CableRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useUnit } from 'effector-react';

import { apartmentsApi } from '../api/apartments';
import { buildingsApi } from '../api/buildings';
import { devicesApi } from '../api/devices';
import { deviceDocumentsApi } from '../api/deviceDocuments';
import { communicationEquipmentApi } from '../api/communicationEquipment';
import AddDeviceDialog from '../components/Devices/AddDeviceDialog';
import DeviceConnectionHubDialog from '../components/Devices/DeviceConnectionHubDialog';
import ReplaceDeviceDialog from '../components/Devices/ReplaceDeviceDialog';
import { $canManageDevices, $isSettlementTenant } from '../store/auth';
import { getObjectKindLabels } from '../utils/complexKind';

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid rgba(38, 65, 94, 0.12)',
  background: '#fff',
  boxShadow: 'none',
};

const softCardSx = {
  ...surfaceSx,
  p: 1.5,
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)',
};

const placementFieldSx = {
  '& .MuiInputLabel-root': { fontSize: 13, color: '#64748B' },
  '& .MuiOutlinedInput-root': {
    minHeight: 42,
    borderRadius: 2,
    background: '#F8FAFD',
    '& fieldset': { borderColor: 'rgba(38, 65, 94, 0.16)' },
    '&:hover fieldset': { borderColor: 'rgba(46, 115, 184, 0.48)' },
    '&.Mui-focused fieldset': { borderColor: '#2E73B8', borderWidth: 1 },
  },
};

const placementAutocompleteProps = {
  paper: {
    sx: {
      mt: 0.5,
      borderRadius: 2,
      border: '1px solid rgba(38, 65, 94, 0.16)',
      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
    },
  },
  listbox: {
    sx: {
      p: 0.5,
      maxHeight: 300,
      '& .MuiAutocomplete-option': {
        minHeight: 38,
        mx: 0.25,
        my: 0.15,
        borderRadius: 1.25,
        fontSize: 13,
      },
      '& .MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: 'rgba(46, 115, 184, 0.1)',
      },
      '& .MuiAutocomplete-option.Mui-focused': {
        backgroundColor: 'rgba(46, 115, 184, 0.07)',
      },
    },
  },
};

function getBuildingPlacementLabel(building) {
  if (!building) return '';
  return [building.name, building.address]
    .filter((value, index, values) => value && values.indexOf(value) === index)
    .join(' · ');
}

const tableHeadCellSx = {
  py: 0.9,
  px: 1.1,
  fontSize: 12,
  fontWeight: 800,
  color: '#526579',
  background: '#F7FAFE',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  whiteSpace: 'nowrap',
};

const tableCellSx = {
  py: 0.9,
  px: 1.1,
  fontSize: 13,
  borderColor: 'rgba(15, 23, 42, 0.06)',
  verticalAlign: 'middle',
};

const statusLabels = {
  new: 'Новая',
  in_review: 'На проверке',
  assigned: 'Ждёт исполнителя',
  in_progress: 'В работе',
  waiting_for_resident: 'Ждёт жильца',
  waiting_for_contractor: 'Ждёт подрядчика',
  resolved: 'Выполнена',
  closed: 'Закрыта',
  cancelled: 'Отменена',
};

const priorityLabels = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочно',
};

const documentTypeLabels = {
  passport: 'Паспорт прибора',
  seal_act: 'Акт пломбировки',
  verification: 'Свидетельство о поверке',
  replacement_act: 'Акт замены',
  photo: 'Фото прибора',
  other: 'Другое',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU');
}

function formatNumber(value, digits = 3) {
  if (value == null || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString('ru-RU', {
    maximumFractionDigits: digits,
  });
}

const readingSourceLabels = {
  AUTO: 'Автоматически',
  MANUAL: 'Введено вручную',
  MOBILE: 'Мобильное приложение',
  IMPORT: 'Импорт',
  TELEGRAM: 'Telegram',
  EDGE_AGENT: 'УСПД',
  STAFF_USB: 'Сотрудник, USB',
  STAFF_USB_ARCHIVE: 'Архив прибора, USB',
  COPIED_PREVIOUS: 'Предыдущее значение',
};

function getReadingUnit(systemType) {
  if (systemType === 'ХВС' || systemType === 'ГВС') return 'м³';
  if (systemType === 'Отопление') return 'Гкал';
  if (systemType === 'УРТ') return 'R';
  return '';
}

function getReadingDetails(device) {
  const components = device?.latest_components;
  if (!components || typeof components !== 'object') return [];

  const definitions = device.system_type === 'УРТ'
    ? [
        ['allocation_previous_period', 'Прошлый период', 'R'],
        ['radiator_temperature_c', 'Радиатор', '°C'],
        ['ambient_temperature_c', 'Помещение', '°C'],
        ['error_raw', 'Ошибки', ''],
        ['rssi', 'RSSI', 'dBm'],
      ]
    : device.system_type === 'Отопление'
      ? [
          ['volume_current', 'Объём', 'м³'],
          ['cold_energy_current', 'Холод', 'Гкал'],
          ['operating_hours_current', 'Наработка', 'ч'],
          ['errors_current', 'Ошибки', ''],
          ['rssi', 'RSSI', 'dBm'],
        ]
      : [
          ['hourly', 'Час', 'м³'],
          ['daily', 'Сутки', 'м³'],
          ['monthly', 'Месяц', 'м³'],
          ['errors_current', 'Ошибки', ''],
          ['rssi', 'RSSI', 'dBm'],
        ];

  return definitions.flatMap(([key, label, unit]) => {
    const value = components[key];
    if (value == null || (key.includes('error') && Number(value) === 0)) return [];
    return [{ key, label, value, unit }];
  });
}

function formatArea(value) {
  if (!value) return '—';
  const number = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(number)) return String(value);
  return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} м²`;
}

function formatFileSize(value) {
  const size = Number(value || 0);
  if (!size) return 'Файл';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

function getDeviceTone(device) {
  const systemType = String(device?.system_type || '').toLowerCase();
  if (systemType.includes('гвс')) return '#F97316';
  if (systemType.includes('хвс')) return '#2E73B8';
  if (systemType.includes('электро')) return '#D99A0B';
  if (systemType.includes('урт')) return '#7C3AED';
  if (systemType.includes('отоп')) return '#DC2626';
  return '#526579';
}

function getDeviceIcon(device) {
  const systemType = String(device?.system_type || '').toLowerCase();
  if (systemType.includes('электро')) return <ElectricBoltRoundedIcon fontSize="small" />;
  if (systemType.includes('урт')) return <DeviceThermostatRoundedIcon fontSize="small" />;
  if (systemType.includes('отоп') || systemType.includes('гвс')) return <LocalFireDepartmentRoundedIcon fontSize="small" />;
  return <OpacityRoundedIcon fontSize="small" />;
}

function getReadingFreshness(device) {
  if (!device?.latest_reading_ts) {
    return { label: 'Нет показаний', value: 0, color: '#DC2626', state: 'bad' };
  }

  const readingDate = new Date(device.latest_reading_ts);
  if (Number.isNaN(readingDate.getTime())) {
    return { label: 'Дата не определена', value: 0, color: '#DC2626', state: 'bad' };
  }

  const days = Math.max(0, Math.floor((Date.now() - readingDate.getTime()) / 86400000));
  if (days <= 3) return { label: 'Показания в норме', value: 100, color: '#0F9F6E', state: 'good' };
  if (days <= 14) return { label: `${days} дн. назад`, value: 72, color: '#2E73B8', state: 'ok' };
  if (days <= 35) return { label: `${days} дн. назад`, value: 44, color: '#F97316', state: 'warn' };
  return { label: `${days} дн. назад`, value: 18, color: '#DC2626', state: 'bad' };
}

function buildDeviceGroups(devices) {
  return (devices || []).reduce((acc, device) => {
    const key = device.system_type || 'Без типа';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

function getDeviceConnectionLabel(device) {
  if (isLoRaWanDevice(device)) return 'LoRaWAN';
  if (device?.mqtt_topic) return 'MQTT';
  if (device?.reading_collection_mode === 'file_import') return 'Импорт из файла';
  if (device?.reading_collection_mode === 'automatic') return 'Автоматический сбор';
  if (device?.decoder) return device.decoder;
  return 'Канал не указан';
}

function isLoRaWanDevice(device) {
  return Boolean(device?.lorawan_enabled || device?.dev_eui);
}

const lorawanStatusMeta = {
  synced: { label: 'Синхронизирован', color: 'success' },
  ready: { label: 'Готов', color: 'primary' },
  pending: { label: 'В очереди', color: 'warning' },
  profile_missing: { label: 'Нет профиля', color: 'warning' },
  not_configured: { label: 'Не настроено', color: 'warning' },
  sync_error: { label: 'Ошибка', color: 'error' },
  external_conflict: { label: 'Конфликт', color: 'error' },
  external_disabled: { label: 'Отключено снаружи', color: 'warning' },
  disabled: { label: 'Отключено', color: 'default' },
};

function getLoRaWanStatusMeta(device) {
  const status = device?.lorawan_sync_status || (device?.lorawan_enabled ? 'pending' : 'disabled');
  return lorawanStatusMeta[status] || { label: status, color: 'default' };
}

function formatRadioMetric(value, suffix) {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${number.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}${suffix}`;
}

function LoRaWanMetric({ label, value }) {
  return (
    <Box
      sx={{
        minWidth: 86,
        px: 1,
        py: 0.75,
        borderRadius: 2,
        border: '1px solid rgba(46, 115, 184, 0.16)',
        backgroundColor: '#fff',
      }}
    >
      <Typography sx={{ fontSize: 10, color: '#64748B', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#1F3550', fontWeight: 900, lineHeight: 1.15 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function DeviceMetaItem({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.5, color: '#7A8CA0', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: '#1F3550', fontWeight: 750, lineHeight: 1.2 }} noWrap>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function StatCard({ icon, label, value, tone = '#2E73B8' }) {
  return (
    <Paper variant="outlined" sx={softCardSx}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            color: tone,
            background: `${tone}14`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.1, color: '#1F3550' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ py: 0.85, borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}
    >
      <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#1F3550', fontWeight: 750, textAlign: 'right' }}>
        {value || '—'}
      </Typography>
    </Stack>
  );
}

function extractPollReadout(data) {
  const endpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
  for (const endpoint of endpoints) {
    const devices = Array.isArray(endpoint?.devices) ? endpoint.devices : [];
    const hit = devices.find((item) => item?.readout);
    if (hit) {
      return {
        ...hit.readout,
        readSerial: hit.read_serial || hit.readout?.secondary_id || '',
        latencyMs: hit.latency_ms,
      };
    }
  }
  return null;
}

function PollReadout({ readout }) {
  const values = Array.isArray(readout?.values) ? readout.values : [];
  if (!readout || !values.length) return null;
  return (
    <Box
      sx={{
        mt: 0.25,
        p: 1,
        borderRadius: 2.25,
        border: '1px solid rgba(15, 159, 110, 0.2)',
        background: 'rgba(15, 159, 110, 0.045)',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography sx={{ color: '#147D55', fontSize: 12.5, fontWeight: 900 }}>
          Считано с прибора
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          {readout.latencyMs != null ? `${readout.latencyMs} мс` : null}
        </Typography>
      </Stack>
      <Grid container spacing={0.65}>
        {values.map((item, index) => (
          <Grid item xs={6} sm={4} key={`${item.code || 'value'}-${index}`}>
            <Box sx={{ px: 0.85, py: 0.65, borderRadius: 1.6, background: '#FFFFFF' }}>
              <Typography sx={{ color: '#64748B', fontSize: 10.5, fontWeight: 750, lineHeight: 1.15 }}>
                {item.label || 'Значение'}
              </Typography>
              <Typography sx={{ color: '#1F3550', fontSize: 13, fontWeight: 900, lineHeight: 1.3 }}>
                {item.display_value ?? item.value ?? '—'}{item.unit ? ` ${item.unit}` : ''}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography sx={{ mt: 0.65, color: '#64748B', fontSize: 10.5 }}>
        {[
          readout.readSerial ? `№ ${readout.readSerial}` : null,
          readout.measured_at ? `время прибора ${String(readout.measured_at).replace('T', ' ')}` : null,
        ].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  );
}

function DeviceCard({
  device,
  highlighted = false,
  pulseConnection = null,
  onOpenDocuments,
  onOpenLoRaWanDiagnostics,
  onSyncLoRaWan,
  onDisableLoRaWan,
  onOpenConnection,
  onOpenPlacement,
  onReplace,
  onPollDevice,
  pollBusy,
  pollFeedback,
  lorawanBusy,
}) {
  const tone = getDeviceTone(device);
  const freshness = getReadingFreshness(device);
  const documentsCount = Number(device.documents_count || 0);
  const isLoRaWan = isLoRaWanDevice(device);
  const lorawanMeta = getLoRaWanStatusMeta(device);
  const readingUnit = getReadingUnit(device.system_type);
  const readingSource = readingSourceLabels[device.latest_reading_source]
    || device.latest_reading_source;
  const readingDetails = getReadingDetails(device);
  const placementLocked = Boolean(device.heat_archived) || device.status === 'REPLACED';
  const hasLoRaWanRadioMetrics = (
    device.lorawan_signal_rssi != null ||
    device.lorawan_signal_snr != null ||
    device.lorawan_gateways_count != null
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3.25,
        border: highlighted ? '1px solid rgba(37, 99, 235, 0.42)' : '1px solid rgba(38, 65, 94, 0.13)',
        background: highlighted
          ? 'linear-gradient(180deg, rgba(239,246,255,0.98) 0%, #FFFFFF 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 100%)',
        boxShadow: highlighted ? '0 0 0 4px rgba(37, 99, 235, 0.08)' : 'none',
        overflow: 'hidden',
        transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
      }}
    >
      <Stack spacing={1.15} sx={{ p: 1.35 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.4,
                display: 'grid',
                placeItems: 'center',
                color: tone,
                background: `${tone}14`,
                border: `1px solid ${tone}1F`,
              }}
            >
              {getDeviceIcon(device)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: '#1F3550', fontWeight: 900, lineHeight: 1.1 }}>
                {device.system_type || device.name || 'Прибор'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B' }} noWrap>
                {device.serial_number || 'Серийный номер не указан'}
              </Typography>
            </Box>
          </Stack>
          {device.heat_archived ? (
            <Chip size="small" label="Архив" sx={{ height: 24, borderRadius: 999, fontWeight: 800 }} />
          ) : null}
        </Stack>

        {pulseConnection ? (
          <Chip
            size="small"
            icon={<CableRoundedIcon sx={{ fontSize: '16px !important' }} />}
            color={pulseConnection.pulse_weight == null ? 'warning' : 'success'}
            variant="outlined"
            label={
              pulseConnection.pulse_weight == null
                ? `${pulseConnection.module_name} · канал ${pulseConnection.channel_number} · без веса`
                : `${pulseConnection.module_name} · канал ${pulseConnection.channel_number}`
            }
            sx={{ height: 26, borderRadius: 999, fontWeight: 800, alignSelf: 'flex-start', maxWidth: '100%' }}
          />
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box
            sx={{
              minWidth: 118,
              p: 1,
              borderRadius: 2.25,
              background: '#F6F9FD',
              border: '1px solid rgba(38, 65, 94, 0.08)',
            }}
          >
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
              Последнее показание
            </Typography>
            <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 29, lineHeight: 1, letterSpacing: '-0.045em' }}>
              {formatNumber(device.latest_reading_value)}
              {device.latest_reading_value != null && readingUnit ? (
                <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 800, letterSpacing: 0, color: '#64748B' }}>
                  {readingUnit}
                </Box>
              ) : null}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {formatDate(device.latest_reading_ts)}
            </Typography>
            {readingSource ? (
              <Typography sx={{ mt: 0.25, color: '#64748B', fontSize: 10.5, fontWeight: 750 }}>
                {readingSource}
              </Typography>
            ) : null}
          </Box>

          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.85}
              alignItems="center"
              sx={{
                px: 1,
                py: 0.85,
                borderRadius: 2.25,
                color: freshness.color,
                background: '#fff',
                border: `1px solid ${freshness.color}2E`,
              }}
            >
              {freshness.state === 'good' ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 19 }} />
              ) : (
                <WarningAmberRoundedIcon sx={{ fontSize: 19 }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 900, lineHeight: 1.15 }}>
                  {freshness.state === 'good' ? 'Показания актуальны' : freshness.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748B', lineHeight: 1.15 }}>
                  {freshness.state === 'good' ? 'Данные получены недавно' : 'Требуется проверка передачи'}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={0.75}>
              <Grid item xs={6}>
                <DeviceMetaItem label="Связь" value={getDeviceConnectionLabel(device)} />
              </Grid>
              <Grid item xs={6}>
                <DeviceMetaItem label="Производитель" value={device.manufacturer} />
              </Grid>
              <Grid item xs={6}>
                <DeviceMetaItem label="Пломбировка" value={formatDateOnly(device.seal_date)} />
              </Grid>
              <Grid item xs={6}>
                <DeviceMetaItem label="Поверка" value={formatDateOnly(device.verification_date)} />
              </Grid>
            </Grid>
          </Stack>
        </Stack>

        {readingDetails.length ? (
          <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
            {readingDetails.map((item) => (
              <Chip
                key={item.key}
                size="small"
                variant="outlined"
                label={`${item.label}: ${formatNumber(item.value)}${item.unit ? ` ${item.unit}` : ''}`}
                sx={{ height: 25, borderRadius: 1.5, fontWeight: 750, color: '#415A74' }}
              />
            ))}
          </Stack>
        ) : null}

        {device.latest_replacement_at ? (
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            Последняя замена: {formatDate(device.latest_replacement_at)}
            {device.latest_replacement_reason ? ` · ${device.latest_replacement_reason}` : ''}
          </Typography>
        ) : null}

        {isLoRaWan ? (
          <Stack
            spacing={0.8}
            sx={{
              p: 1,
              borderRadius: 2.25,
              background: '#F7FAFC',
              border: '1px solid rgba(38, 65, 94, 0.09)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <LanRoundedIcon sx={{ fontSize: 18, color: '#2E73B8' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: '#1F3550', lineHeight: 1.1 }}>
                    BillingService LoRaWAN
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748B' }} noWrap>
                    {device.dev_eui || 'DevEUI не указан'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                label={lorawanMeta.label}
                color={lorawanMeta.color}
                variant="outlined"
                sx={{ height: 23, fontWeight: 800 }}
              />
            </Stack>
            {device.lorawan_sync_error ? (
              <Typography sx={{ fontSize: 11.5, color: '#B42318', fontWeight: 700 }}>
                {device.lorawan_sync_error}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <LoRaWanMetric label="RSSI" value={formatRadioMetric(device.lorawan_signal_rssi, ' dBm')} />
              <LoRaWanMetric label="SNR" value={formatRadioMetric(device.lorawan_signal_snr, ' dB')} />
              <LoRaWanMetric
                label="Базовые станции"
                value={device.lorawan_gateways_count != null ? `${device.lorawan_gateways_count}` : null}
              />
            </Stack>
            {!hasLoRaWanRadioMetrics ? (
              <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                Радиометрики появятся после первого принятого сообщения от прибора.
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 11, color: '#64748B', flex: 1, minWidth: 120 }}>
                Проверка готовности: {formatDate(device.lorawan_last_sync_at)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SyncRoundedIcon sx={{ fontSize: 15 }} />}
                disabled={lorawanBusy || !device.dev_eui}
                onClick={() => onSyncLoRaWan?.(device)}
                sx={{ minHeight: 28, borderRadius: 1.5, textTransform: 'none', fontSize: 11.5, fontWeight: 800 }}
              >
                Проверить готовность
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={!device.dev_eui}
                onClick={() => onOpenLoRaWanDiagnostics?.(device)}
                sx={{ minHeight: 28, borderRadius: 1.5, textTransform: 'none', fontSize: 11.5, fontWeight: 800 }}
              >
                Диагностика
              </Button>
              {device.lorawan_enabled ? (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  disabled={lorawanBusy}
                  onClick={() => onDisableLoRaWan?.(device)}
                  sx={{ minHeight: 28, borderRadius: 1.5, textTransform: 'none', fontSize: 11.5, fontWeight: 800 }}
                >
                  Отключить
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        <Divider sx={{ borderColor: 'rgba(15, 23, 42, 0.06)' }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
        >
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {device.heat_coefficient != null ? (
              <Chip size="small" label={`Коэф. ${formatNumber(device.heat_coefficient)}`} variant="outlined" />
            ) : null}
            <Chip
              size="small"
              label={device.replacements_count > 0 ? `Замен: ${device.replacements_count}` : 'Замен не было'}
              variant="outlined"
              sx={device.replacements_count > 0 ? { borderColor: 'rgba(249, 115, 22, 0.35)', color: '#C05621' } : null}
            />
          </Stack>

          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
            {onReplace ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<PublishedWithChangesRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => onReplace(device)}
                sx={{
                  minHeight: 30,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Заменить
              </Button>
            ) : null}
            {onOpenPlacement ? (
              <Tooltip
                title={
                  placementLocked
                    ? 'Архивный прибор остаётся в истории квартиры'
                    : 'Перенести или убрать из квартиры'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DriveFileMoveRoundedIcon sx={{ fontSize: 16 }} />}
                    disabled={placementLocked}
                    onClick={() => onOpenPlacement(device)}
                    sx={{ minHeight: 30, borderRadius: 1.5, textTransform: 'none', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}
                  >
                    Размещение
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {device.bus_agent_connected ? (
              <Tooltip
                title={
                  device.bus_agent_online
                    ? `Опросить прибор через ${device.bus_agent_name || 'агент'}`
                    : 'Агент УСПД сейчас не в сети'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      pollBusy
                        ? <CircularProgress size={14} color="inherit" />
                        : <SensorsRoundedIcon sx={{ fontSize: 16 }} />
                    }
                    disabled={pollBusy || !device.bus_agent_online}
                    onClick={() => onPollDevice?.(device)}
                    sx={{
                      minHeight: 30,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 800,
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pollBusy ? 'Опрос…' : 'Опросить'}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {onOpenConnection ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsInputAntennaRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => onOpenConnection(device)}
                sx={{ minHeight: 30, borderRadius: 1.5, textTransform: 'none', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                Подключение
              </Button>
            ) : null}
            <Button
              size="small"
              variant={documentsCount > 0 ? 'contained' : 'outlined'}
              startIcon={<DescriptionRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => onOpenDocuments?.(device)}
              sx={{
                minHeight: 30,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 800,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                ...(documentsCount > 0
                  ? {
                      background: '#2E73B8',
                      '&:hover': { background: '#245B92', boxShadow: 'none' },
                    }
                  : {}),
              }}
            >
              Документы: {documentsCount}
            </Button>
          </Stack>
        </Stack>
        {pollFeedback ? (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: pollFeedback.error ? '#B42318' : '#147D55',
                fontWeight: 750,
                textAlign: 'right',
              }}
            >
              {pollFeedback.text}
            </Typography>
            <PollReadout readout={pollFeedback.readout} />
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ArchivedDeviceCard({ device, onOpenDocuments }) {
  const readingUnit = getReadingUnit(device.system_type);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.1,
        borderRadius: 2.5,
        borderColor: 'rgba(100, 116, 139, 0.18)',
        background: '#F8FAFC',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: '#64748B',
            background: '#E9EEF4',
            flexShrink: 0,
          }}
        >
          {getDeviceIcon(device)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography sx={{ color: '#334155', fontSize: 13.5, fontWeight: 850 }}>
              {device.system_type || device.name || 'Прибор'}
            </Typography>
            <Chip size="small" label="Заменён" sx={{ height: 21, fontSize: 10.5, fontWeight: 800 }} />
          </Stack>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            № {device.serial_number || 'не указан'}
            {device.heat_removal_date ? ` · снят ${formatDateOnly(device.heat_removal_date)}` : ''}
          </Typography>
        </Box>
        {device.latest_reading_value != null ? (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ color: '#334155', fontSize: 14, fontWeight: 850 }}>
              {formatNumber(device.latest_reading_value)}{readingUnit ? ` ${readingUnit}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              {formatDate(device.latest_reading_ts)}
            </Typography>
          </Box>
        ) : null}
        {Number(device.documents_count || 0) > 0 ? (
          <Tooltip title="Документы прибора">
            <IconButton size="small" onClick={() => onOpenDocuments(device)}>
              <DescriptionRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

function DevicePlacementDialog({
  open,
  device,
  buildingId,
  currentApartmentId,
  currentBuildingName,
  currentApartmentLabel,
  labels,
  onClose,
  onChanged,
}) {
  const [buildings, setBuildings] = useState([]);
  const [targetBuildingId, setTargetBuildingId] = useState('');
  const [apartments, setApartments] = useState([]);
  const [targetApartmentId, setTargetApartmentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDetach, setConfirmDetach] = useState(false);
  const selectedBuilding = buildings.find(
    (item) => String(item.id) === String(targetBuildingId)
  ) || null;
  const selectedApartment = apartments.find(
    (item) => String(item.apartment_id) === String(targetApartmentId)
  ) || null;

  const getApartmentLabel = (item) => {
    if (!item) return '';
    return [
      `${labels.apartmentShort} ${item.display_number}`,
      item.corp_name && item.corp_name !== 'Без корпуса' ? item.corp_name : null,
      item.section_number ? `подъезд ${item.section_number}` : null,
      item.floor_number ? `этаж ${item.floor_number}` : null,
    ].filter(Boolean).join(' · ');
  };

  useEffect(() => {
    let cancelled = false;
    if (!open || !buildingId) {
      setBuildings([]);
      setTargetBuildingId('');
      setApartments([]);
      setTargetApartmentId('');
      setError('');
      setConfirmDetach(false);
      return undefined;
    }

    const loadBuildings = async () => {
      setLoading(true);
      setError('');
      try {
        const items = await buildingsApi.getBuildings(0, 1000);
        if (!cancelled) {
          setBuildings(
            [...(Array.isArray(items) ? items : [])].sort((left, right) =>
              getBuildingPlacementLabel(left).localeCompare(
                getBuildingPlacementLabel(right),
                'ru',
                { numeric: true, sensitivity: 'base' }
              )
            )
          );
          setTargetBuildingId(String(buildingId));
        }
      } catch (loadError) {
        if (!cancelled) {
          setBuildings([]);
          setError(loadError?.response?.data?.detail || 'Не удалось загрузить список домов.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadBuildings();
    return () => {
      cancelled = true;
    };
  }, [open, buildingId]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !targetBuildingId) return undefined;

    const loadApartments = async () => {
      setLoading(true);
      setError('');
      setTargetApartmentId('');
      setConfirmDetach(false);
      try {
        const summary = await apartmentsApi.getBuildingSummary(targetBuildingId);
        if (!cancelled) {
          setApartments(
            (summary?.items || []).filter(
              (item) => String(item.apartment_id) !== String(currentApartmentId)
            )
          );
        }
      } catch (loadError) {
        if (!cancelled) {
          setApartments([]);
          setError(loadError?.response?.data?.detail || `Не удалось загрузить ${labels.premises.toLowerCase()}.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadApartments();
    return () => {
      cancelled = true;
    };
  }, [open, targetBuildingId, currentApartmentId, labels.premises]);

  const finish = async (action) => {
    if (!device?.id || saving) return;
    if (action === 'move' && !targetApartmentId) {
      setError(`Выберите ${labels.apartment.toLowerCase()}.`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (action === 'move') {
        await devicesApi.moveApartment(device.id, targetApartmentId);
      } else {
        await devicesApi.detachApartment(device.id);
      }
      await onChanged?.(
        action === 'move'
          ? `Прибор перемещён в выбранную ${labels.apartment.toLowerCase()}.`
          : `Прибор убран из ${labels.apartment.toLowerCase()}.`
      );
      onClose?.();
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || 'Не удалось изменить размещение прибора.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ px: 2.25, py: 1.75 }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2.25,
              color: '#2E73B8',
              background: '#EEF5FC',
            }}
          >
            <DriveFileMoveRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ color: '#1F3550', fontWeight: 900, lineHeight: 1.15 }}>
              Размещение прибора
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {device?.serial_number ? `№ ${device.serial_number}` : device?.name || 'Без серийного номера'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5, background: '#F7F9FC' }}>
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, borderColor: 'rgba(38, 65, 94, 0.12)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.4 }}>
              <Typography sx={{ color: '#1F3550', fontWeight: 850, fontSize: 14 }}>
                {device?.system_type || device?.name || 'Прибор'}
              </Typography>
              <Chip
                size="small"
                label={[currentBuildingName, currentApartmentLabel].filter(Boolean).join(' · ')}
                sx={{ maxWidth: '70%', height: 24, fontWeight: 750, color: '#415A74' }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: '#64748B' }}>
              Показания, документы и настройки прибора сохранятся.
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Для физической замены прибора используйте оформление замены.
            </Typography>
          </Paper>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, borderColor: 'rgba(38, 65, 94, 0.12)' }}>
            <Stack spacing={1.1}>
              <Typography sx={{ color: '#1F3550', fontWeight: 850, fontSize: 14 }}>
                Перенести
              </Typography>
              <Autocomplete
                size="small"
                options={buildings}
                value={selectedBuilding}
                onChange={(_, value) => setTargetBuildingId(value ? String(value.id) : '')}
                getOptionLabel={getBuildingPlacementLabel}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                loading={loading && !buildings.length}
                disabled={saving}
                noOptionsText="Дома не найдены"
                slotProps={placementAutocompleteProps}
                sx={placementFieldSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={labels.house}
                    placeholder="Найти дом"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && !buildings.length ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                size="small"
                options={apartments}
                value={selectedApartment}
                onChange={(_, value) => setTargetApartmentId(value ? String(value.apartment_id) : '')}
                getOptionLabel={getApartmentLabel}
                isOptionEqualToValue={(option, value) => String(option.apartment_id) === String(value.apartment_id)}
                loading={loading && Boolean(targetBuildingId)}
                disabled={saving || !targetBuildingId}
                noOptionsText={`Других ${labels.premises.toLowerCase()} нет`}
                slotProps={placementAutocompleteProps}
                sx={placementFieldSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={labels.apartment}
                    placeholder={`Найти ${labels.apartment.toLowerCase()}`}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && targetBuildingId ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {device?.bus_agent_connected && String(targetBuildingId) !== String(buildingId) ? (
                <Alert severity="info" sx={{ py: 0.25 }}>
                  Для переноса в другой дом сначала закройте подключение к линии УСПД.
                </Alert>
              ) : null}
              <Button
                variant="contained"
                disabled={
                  saving
                  || loading
                  || !targetApartmentId
                  || (device?.bus_agent_connected && String(targetBuildingId) !== String(buildingId))
                }
                onClick={() => finish('move')}
                sx={{ alignSelf: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
              >
                {saving ? 'Сохранение...' : 'Переместить'}
              </Button>
            </Stack>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              borderColor: confirmDetach ? 'rgba(211, 47, 47, 0.35)' : 'rgba(38, 65, 94, 0.12)',
              background: confirmDetach ? '#FFF9F8' : '#FFFFFF',
            }}
          >
            <Typography sx={{ color: '#1F3550', fontWeight: 800 }}>
              Прибор добавлен ошибочно
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
              Прибор останется в доме в списке непривязанных приборов.
            </Typography>
            {confirmDetach ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Button
                  color="error"
                  variant="contained"
                  disabled={saving}
                  onClick={() => finish('detach')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
                >
                  Подтвердить
                </Button>
                <Button
                  color="inherit"
                  disabled={saving}
                  onClick={() => setConfirmDetach(false)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 750 }}
                >
                  Отмена
                </Button>
              </Stack>
            ) : (
              <Button
                color="error"
                variant="outlined"
                disabled={saving}
                onClick={() => setConfirmDetach(true)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
              >
                {`Убрать из ${labels.apartment.toLowerCase()}`}
              </Button>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceDocumentsDialog({ open, device, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentType, setDocumentType] = useState('passport');
  const [error, setError] = useState('');

  const loadDocuments = useCallback(async () => {
    if (!device?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await deviceDocumentsApi.list(device.id);
      setItems(Array.isArray(data) ? data : []);
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Не удалось загрузить документы.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [device?.id]);

  useEffect(() => {
    if (open && device?.id) {
      loadDocuments();
    } else {
      setItems([]);
      setError('');
      setUploadProgress(0);
    }
  }, [open, device?.id, loadDocuments]);

  const uploadFileToPresignedUrl = (uploadUrl, file) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      if (file.type) {
        xhr.setRequestHeader('Content-Type', file.type);
      }
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        setUploadProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        reject(new Error('Не удалось загрузить файл в хранилище'));
      };
      xhr.onerror = () => reject(new Error('Не удалось загрузить файл в хранилище'));
      xhr.send(file);
    });

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!device?.id || !file) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const presign = await deviceDocumentsApi.createPresign(device.id, {
        file_name: file.name,
        document_type: documentType,
        content_type: file.type || null,
        size_bytes: file.size,
      });
      await uploadFileToPresignedUrl(presign.upload_url, file);
      await deviceDocumentsApi.create(device.id, {
        document_type: documentType,
        storage_bucket: presign.storage_bucket,
        storage_key: presign.storage_key,
        file_name: file.name,
        content_type: file.type || null,
        size_bytes: file.size,
      });
      await loadDocuments();
      onChanged?.();
    } catch (uploadError) {
      setError(uploadError?.response?.data?.detail || uploadError?.message || 'Не удалось загрузить документ.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (documentId) => {
    if (!device?.id || !documentId) return;
    try {
      const data = await deviceDocumentsApi.getDownloadUrl(device.id, documentId);
      if (data?.download_url) {
        window.open(data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (downloadError) {
      setError(downloadError?.response?.data?.detail || 'Не удалось открыть документ.');
    }
  };

  const handleDelete = async (documentId) => {
    if (!device?.id || !documentId) return;
    try {
      await deviceDocumentsApi.remove(device.id, documentId);
      await loadDocuments();
      onChanged?.();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.detail || 'Не удалось удалить документ.');
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850, color: '#1F3550' }}>
        Документы прибора
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1F3550' }}>
              {device?.system_type || 'Прибор'} · {device?.serial_number || 'без номера'}
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="Тип документа"
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
            >
              {Object.entries(documentTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadFileRoundedIcon />}
              disabled={uploading}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
            >
              {uploading ? 'Загрузка...' : 'Загрузить'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileSelect}
              />
            </Button>
          </Stack>

          {uploading ? (
            <LinearProgress
              variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgress || 0}
              sx={{ height: 6, borderRadius: 999 }}
            />
          ) : null}

          <Stack spacing={0.75}>
            {loading ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : items.length ? (
              items.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 1, borderRadius: 2, borderColor: 'rgba(38, 65, 94, 0.12)' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DescriptionRoundedIcon sx={{ color: '#2E73B8' }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 800, color: '#1F3550' }}>
                        {item.file_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {documentTypeLabels[item.document_type] || 'Документ'} · {formatFileSize(item.size_bytes)} · {formatDate(item.created_at)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleDownload(item.id)}>
                      <DownloadRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))
            ) : (
              <Box sx={{ py: 3, textAlign: 'center', color: '#64748B' }}>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>
                  Документы не добавлены
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeviceLoRaWanDiagnosticsDialog({ open, device, onClose }) {
  const [summary, setSummary] = useState(null);
  const [uplinks, setUplinks] = useState([]);
  const [downlinks, setDownlinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadDiagnostics = async () => {
      if (!open || !device?.id) return;
      setLoading(true);
      setError('');
      try {
        const [summaryData, uplinkData, downlinkData] = await Promise.all([
          devicesApi.getLoRaWanDiagnostics(device.id),
          devicesApi.getLoRaWanUplinks(device.id, 12),
          devicesApi.getLoRaWanDownlinks(device.id, 12),
        ]);
        if (!cancelled) {
          setSummary(summaryData);
          setUplinks(Array.isArray(uplinkData) ? uplinkData : []);
          setDownlinks(Array.isArray(downlinkData) ? downlinkData : []);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.response?.data?.detail || 'Не удалось загрузить диагностику LoRaWAN.');
          setSummary(null);
          setUplinks([]);
          setDownlinks([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDiagnostics();
    return () => {
      cancelled = true;
    };
  }, [open, device?.id]);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setUplinks([]);
      setDownlinks([]);
      setError('');
    }
  }, [open]);

  const latest = summary?.latest_event || uplinks[0] || null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LanRoundedIcon sx={{ color: '#2E73B8' }} />
          <Box>
            <Typography sx={{ fontWeight: 900, color: '#1F3550', lineHeight: 1.1 }}>
              Диагностика LoRaWAN
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {device?.serial_number || device?.dev_eui || 'Прибор'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#F8FAFC' }}>
        <Stack spacing={1.25}>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      Событий
                    </Typography>
                    <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 24 }}>
                      {summary?.events_count ?? uplinks.length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      Последний RSSI
                    </Typography>
                    <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 24 }}>
                      {formatRadioMetric(latest?.best_rssi, ' dBm')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      Последний SNR
                    </Typography>
                    <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 24 }}>
                      {formatRadioMetric(latest?.best_snr, ' dB')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                      Базовые станции
                    </Typography>
                    <Typography sx={{ color: '#1F3550', fontWeight: 950, fontSize: 24 }}>
                      {latest?.gateway_count ?? '—'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, maxHeight: 430 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Время</TableCell>
                      <TableCell sx={tableHeadCellSx}>fCnt</TableCell>
                      <TableCell sx={tableHeadCellSx}>RSSI</TableCell>
                      <TableCell sx={tableHeadCellSx}>SNR</TableCell>
                      <TableCell sx={tableHeadCellSx}>БС</TableCell>
                      <TableCell sx={tableHeadCellSx}>Детали</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uplinks.map((event) => (
                      <TableRow key={event.id} hover>
                        <TableCell sx={tableCellSx}>{formatDate(event.received_at)}</TableCell>
                        <TableCell sx={tableCellSx}>{event.f_cnt ?? '—'}</TableCell>
                        <TableCell sx={tableCellSx}>{formatRadioMetric(event.best_rssi, ' dBm')}</TableCell>
                        <TableCell sx={tableCellSx}>{formatRadioMetric(event.best_snr, ' dB')}</TableCell>
                        <TableCell sx={tableCellSx}>{event.gateway_count}</TableCell>
                        <TableCell sx={{ ...tableCellSx, minWidth: 260 }}>
                          <Stack spacing={0.35}>
                            {(event.gateway_observations || []).slice(0, 4).map((gateway) => (
                              <Stack
                                key={gateway.id}
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                sx={{ fontSize: 12, color: '#526579' }}
                              >
                                <Chip
                                  size="small"
                                  label={gateway.gateway_name || gateway.gateway_id}
                                  variant="outlined"
                                  sx={{ maxWidth: 150, height: 22, fontSize: 11 }}
                                />
                                <span>{formatRadioMetric(gateway.rssi, ' dBm')}</span>
                                <span>{formatRadioMetric(gateway.snr, ' dB')}</span>
                              </Stack>
                            ))}
                            {(event.gateway_observations || []).length > 4 ? (
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                ещё {(event.gateway_observations || []).length - 4}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!uplinks.length ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ ...tableCellSx, py: 4, textAlign: 'center', color: '#64748B' }}>
                          Диагностических событий пока нет.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, maxHeight: 260 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Downlink/ack</TableCell>
                      <TableCell sx={tableHeadCellSx}>Время</TableCell>
                      <TableCell sx={tableHeadCellSx}>fCntDown</TableCell>
                      <TableCell sx={tableHeadCellSx}>Gateway</TableCell>
                      <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {downlinks.map((event) => (
                      <TableRow key={event.id} hover>
                        <TableCell sx={tableCellSx}>
                          <Chip size="small" label={event.event_type} variant="outlined" />
                        </TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(event.received_at)}</TableCell>
                        <TableCell sx={tableCellSx}>{event.f_cnt_down ?? '—'}</TableCell>
                        <TableCell sx={{ ...tableCellSx, fontFamily: 'monospace', fontSize: 12 }}>
                          {event.gateway_id || '—'}
                        </TableCell>
                        <TableCell sx={tableCellSx}>
                          {event.acked == null ? (
                            <Chip size="small" label="получено" variant="outlined" />
                          ) : (
                            <Chip
                              size="small"
                              label={event.acked ? 'ack' : 'ошибка'}
                              color={event.acked ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!downlinks.length ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ ...tableCellSx, py: 3, textAlign: 'center', color: '#64748B' }}>
                          Downlink/ack событий пока нет.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ApartmentDetailsPage() {
  const { apartmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const focusedDeviceId = searchParams.get('deviceId') || searchParams.get('device_id');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [documentsDevice, setDocumentsDevice] = useState(null);
  const [connectionDevice, setConnectionDevice] = useState(null);
  const [pulseLinks, setPulseLinks] = useState({});
  const [lorawanDiagnosticsDevice, setLorawanDiagnosticsDevice] = useState(null);
  const [lorawanBusyDeviceId, setLorawanBusyDeviceId] = useState(null);
  const [pollingDeviceId, setPollingDeviceId] = useState(null);
  const [pollFeedbackByDevice, setPollFeedbackByDevice] = useState({});
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [placementDevice, setPlacementDevice] = useState(null);
  const [replacementDevice, setReplacementDevice] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [canManageDevices, isSettlementTenant] = useUnit([
    $canManageDevices,
    $isSettlementTenant,
  ]);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );

  const loadOverview = useCallback(
    async ({ silent = false } = {}) => {
      if (!apartmentId) return;
      if (!silent) {
        setLoading(true);
      }
      setError('');
      try {
        const data = await apartmentsApi.getOverview(apartmentId);
        setOverview(data);
        if (data?.building_id) {
          try {
            const modulesPage = await communicationEquipmentApi.listRadioModules(data.building_id, {
              detail: 'full',
              page_size: 100,
            });
            const links = {};
            (modulesPage.items || []).forEach((module) => {
              (module.channels || []).forEach((channel) => {
                if (channel.device?.id) {
                  links[channel.device.id] = {
                    module_name: module.name,
                    channel_number: channel.number,
                    pulse_weight: channel.pulse_weight,
                  };
                }
              });
            });
            setPulseLinks(links);
          } catch {
            setPulseLinks({});
          }
        } else {
          setPulseLinks({});
        }
      } catch (loadError) {
        console.error('load apartment overview failed', loadError);
        if (!silent) {
          setOverview(null);
        }
        setError(`Не удалось загрузить карточку ${labels.apartment.toLowerCase()}.`);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [apartmentId, labels.apartment]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!focusedDeviceId || !overview?.devices?.length) {
      return;
    }
    const timeout = window.setTimeout(() => {
      const element = document.getElementById(`apartment-device-${focusedDeviceId}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timeout);
  }, [archiveOpen, focusedDeviceId, overview?.devices]);

  const apartment = overview?.apartment;
  const heatingMode = useMemo(() => {
    if (!apartment) return '—';
    if (apartment.heat_calculate_by_area) return 'По площади';
    return apartment.heat_accounting_mode || 'По приборам';
  }, [apartment]);
  const activeDevices = useMemo(
    () => (overview?.devices || []).filter(
      (device) => device.status !== 'REPLACED' && !device.heat_archived
    ),
    [overview?.devices]
  );
  const archivedDevices = useMemo(
    () => (overview?.devices || []).filter(
      (device) => device.status === 'REPLACED' || device.heat_archived
    ),
    [overview?.devices]
  );
  const deviceGroups = useMemo(() => buildDeviceGroups(activeDevices), [activeDevices]);
  const readingsCoverage = useMemo(() => {
    const total = overview?.counters?.devices_total || 0;
    if (!total) return 0;
    const ready = total - (overview?.counters?.devices_without_readings || 0);
    return Math.max(0, Math.min(100, Math.round((ready / total) * 100)));
  }, [overview?.counters]);

  useEffect(() => {
    if (
      focusedDeviceId
      && archivedDevices.some((device) => String(device.id) === String(focusedDeviceId))
    ) {
      setArchiveOpen(true);
    }
  }, [archivedDevices, focusedDeviceId]);

  const handleSyncLoRaWan = async (device) => {
    if (!device?.id) return;
    setLorawanBusyDeviceId(device.id);
    setActionError('');
    try {
      await devicesApi.syncLoRaWan(device.id);
      await loadOverview({ silent: true });
    } catch (syncError) {
      setActionError(syncError?.response?.data?.detail || 'Не удалось проверить LoRaWAN-статус устройства.');
    } finally {
      setLorawanBusyDeviceId(null);
    }
  };

  const handleDisableLoRaWan = async (device) => {
    if (!device?.id) return;
    setLorawanBusyDeviceId(device.id);
    setActionError('');
    try {
      await devicesApi.disableLoRaWan(device.id);
      await loadOverview({ silent: true });
    } catch (disableError) {
      setActionError(disableError?.response?.data?.detail || 'Не удалось отключить LoRaWAN для устройства.');
    } finally {
      setLorawanBusyDeviceId(null);
    }
  };

  const handlePollDevice = async (device) => {
    if (!device?.id || pollingDeviceId) return;
    setPollingDeviceId(device.id);
    setActionError('');
    setActionSuccess('');
    try {
      const queued = await devicesApi.pollViaEdgeAgent(device.id);
      setPollFeedbackByDevice((current) => ({
        ...current,
        [device.id]: { text: queued.message || 'Опрос поставлен в очередь.', error: false },
      }));
      setActionSuccess(queued.message || 'Опрос поставлен в очередь.');
      for (let attempt = 0; attempt < 35; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const result = await devicesApi.getEdgeAgentPollResult(device.id, queued.command_id);
        if (result.status === 'completed') {
          const readout = extractPollReadout(result.data);
          setPollFeedbackByDevice((current) => ({
            ...current,
            [device.id]: { text: result.message || 'Прибор успешно опрошен.', error: false, readout },
          }));
          setActionSuccess(result.message || 'Прибор успешно опрошен.');
          await loadOverview({ silent: true });
          return;
        }
        if (result.status === 'failed') {
          setPollFeedbackByDevice((current) => ({
            ...current,
            [device.id]: { text: result.message || 'Прибор не ответил на запрос.', error: true },
          }));
          setActionSuccess('');
          setActionError(result.message || 'Прибор не ответил на запрос.');
          return;
        }
      }
      setActionSuccess('Опрос выполняется агентом. Результат появится в журнале агента.');
    } catch (pollError) {
      const message = pollError?.response?.data?.detail || 'Не удалось запустить опрос прибора.';
      setPollFeedbackByDevice((current) => ({
        ...current,
        [device.id]: { text: message, error: true },
      }));
      setActionError(message);
      setActionSuccess('');
    } finally {
      setPollingDeviceId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '55vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (error || !overview || !apartment) {
    return (
      <Box className="home-container" sx={{ mx: 3, pb: 3 }}>
        <Alert severity="error">
          {error || (isSettlementTenant ? 'Участок не найден.' : 'Квартира не найдена.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="home-container" sx={{ mx: 3, pb: 3 }}>
      <Stack spacing={1.5}>
        {actionError ? <Alert severity="warning" onClose={() => setActionError('')}>{actionError}</Alert> : null}
        {actionSuccess ? <Alert severity="success" onClose={() => setActionSuccess('')}>{actionSuccess}</Alert> : null}

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
          <Stack spacing={0.5}>
            <Button
              size="small"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => {
                const returnTo = location.state?.returnTo;
                if (typeof returnTo === 'string' && returnTo.startsWith('/')) {
                  navigate(returnTo, { replace: true });
                } else {
                  navigate(-1);
                }
              }}
              sx={{ alignSelf: 'flex-start', color: '#526579', fontWeight: 750 }}
            >
              Назад
            </Button>
            <Typography variant="h4" sx={{ color: '#1F3550', fontWeight: 900, letterSpacing: '-0.03em' }}>
              {labels.apartment} {overview.display_number}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 650 }}>
              {[
                overview.building_name || 'Дом',
                overview.corp_is_default || overview.corp_name === 'Без корпуса'
                  ? null
                  : (overview.corp_name || null),
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="flex-end" flexWrap="wrap" useFlexGap>
            <Chip label={apartment.account_number ? `ЛС ${apartment.account_number}` : 'Лицевой счёт не указан'} />
            <Chip label={heatingMode} color={apartment.heat_calculate_by_area ? 'warning' : 'primary'} variant="outlined" />
          </Stack>
        </Stack>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard icon={<SpeedRoundedIcon fontSize="small" />} label="Приборов" value={overview.counters.devices_total} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard icon={<InsightsRoundedIcon fontSize="small" />} label="С показаниями" value={overview.counters.devices_total - overview.counters.devices_without_readings} tone="#0F9F6E" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard icon={<AssignmentRoundedIcon fontSize="small" />} label="Открытых заявок" value={overview.counters.tickets_open} tone="#F97316" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard icon={<WarningAmberRoundedIcon fontSize="small" />} label="Просрочено" value={overview.counters.tickets_overdue} tone="#DC2626" />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'minmax(250px, 30%) minmax(0, 1fr)',
              lg: 'minmax(280px, 320px) minmax(0, 1fr)',
            },
            gap: 1.5,
            alignItems: 'start',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              position: { md: 'sticky' },
              top: { md: 88 },
              maxHeight: { md: 'calc(100vh - 104px)' },
              overflowY: { md: 'auto' },
              alignSelf: { md: 'flex-start' },
              pr: { md: 0.25 },
              // Keep the info column from collapsing under long device lists.
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                background: '#CBD5E1',
                borderRadius: 999,
              },
            }}
          >
            <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <HomeWorkRoundedIcon sx={{ color: '#2E73B8' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 850, color: '#1F3550' }}>
                  Информация
                </Typography>
              </Stack>
              <InfoRow
                label={labels.premise}
                value={`${labels.apartmentShort} ${overview.display_number}`}
              />
              <InfoRow label="Площадь" value={formatArea(apartment.area)} />
              {!isSettlementTenant && (
                <>
                  <InfoRow label="Этаж" value={apartment.floor_number} />
                  <InfoRow label="Секция" value={apartment.section_number} />
                </>
              )}
              <InfoRow label="Собственник" value={apartment.owner_name} />
              <InfoRow label="Лицевой счёт" value={apartment.account_number} />
              <InfoRow label="Пониж. коэффициент" value={formatNumber(apartment.heat_room_coefficient)} />
              <InfoRow label="Ожидаемо радиаторов" value={apartment.heat_expected_radiator_count} />
              <InfoRow
                label={isSettlementTenant ? 'Тип участка' : 'Тип помещения'}
                value={apartment.heat_room_type}
              />
              <InfoRow label="Дата ввода" value={formatDateOnly(apartment.heat_move_in_date)} />
            </Paper>

            <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.5, mt: 1.5 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 850, color: '#1F3550' }}>
                    Контроль
                  </Typography>
                  <Chip
                    size="small"
                    label={`${readingsCoverage}%`}
                    sx={{
                      fontWeight: 850,
                      color: readingsCoverage >= 80 ? '#0F9F6E' : readingsCoverage >= 50 ? '#F97316' : '#DC2626',
                      background: readingsCoverage >= 80 ? '#EAF8F2' : readingsCoverage >= 50 ? '#FFF3E8' : '#FEECEC',
                    }}
                  />
                </Stack>

                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 750 }}>
                      Готовность показаний
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1F3550', fontWeight: 850 }}>
                      {overview.counters.devices_total - overview.counters.devices_without_readings} из {overview.counters.devices_total}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={readingsCoverage}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: '#EAF0F6',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        backgroundColor: readingsCoverage >= 80 ? '#0F9F6E' : readingsCoverage >= 50 ? '#F97316' : '#DC2626',
                      },
                    }}
                  />
                </Box>

                <Stack spacing={0.75}>
                  <InfoRow label="Режим отопления" value={heatingMode} />
                  <InfoRow label="Открытые заявки" value={overview.counters.tickets_open} />
                  <InfoRow label="Просроченные заявки" value={overview.counters.tickets_overdue} />
                </Stack>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/tickets?apartment_id=${apartment.id}`}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    Заявки
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/meter-readings/imports?apartment_id=${apartment.id}`}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    Импорт показаний
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ px: 1.4, py: 1.15 }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 850, color: '#1F3550' }}>
                    Приборы и последние показания
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 650 }}>
                    {overview.counters.devices_without_readings > 0
                      ? `Без показаний: ${overview.counters.devices_without_readings}`
                      : 'Все приборы с показаниями'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`${activeDevices.length} шт.`} />
                  {Object.entries(deviceGroups).map(([label, count]) => (
                    <Chip key={label} size="small" label={`${label}: ${count}`} variant="outlined" />
                  ))}
                  {canManageDevices ? (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => setAddDeviceOpen(true)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
                    >
                      Добавить прибор
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              <Divider />
              <Box sx={{ p: 1.25 }}>
                {activeDevices.length ? (
                  <Grid container spacing={1}>
                    {activeDevices.map((device) => (
                      <Grid
                        id={`apartment-device-${device.id}`}
                        item
                        xs={12}
                        md={6}
                        key={device.id}
                        sx={{ scrollMarginTop: 120 }}
                      >
                        <DeviceCard
                          device={device}
                          highlighted={String(device.id) === String(focusedDeviceId || '')}
                          pulseConnection={pulseLinks[device.id] || null}
                          onOpenDocuments={setDocumentsDevice}
                          onOpenLoRaWanDiagnostics={setLorawanDiagnosticsDevice}
                          onSyncLoRaWan={handleSyncLoRaWan}
                          onDisableLoRaWan={handleDisableLoRaWan}
                          onOpenConnection={canManageDevices ? setConnectionDevice : null}
                          onOpenPlacement={canManageDevices ? setPlacementDevice : null}
                          onReplace={canManageDevices ? (selectedDevice) => {
                            setReplacementDevice({
                              ...selectedDevice,
                              apartment_id: apartment.id,
                              last_data: selectedDevice.latest_reading_value,
                            });
                          } : null}
                          onPollDevice={canManageDevices ? handlePollDevice : null}
                          pollBusy={pollingDeviceId === device.id}
                          pollFeedback={pollFeedbackByDevice[device.id] || null}
                          lorawanBusy={lorawanBusyDeviceId === device.id}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ py: 5, textAlign: 'center', color: '#64748B' }}>
                    <Typography variant="body2" sx={{ fontWeight: 750 }}>
                      Действующие приборы не найдены
                    </Typography>
                  </Box>
                )}

                {archivedDevices.length ? (
                  <Box sx={{ mt: activeDevices.length ? 1.25 : 0 }}>
                    <Button
                      fullWidth
                      color="inherit"
                      onClick={() => setArchiveOpen((value) => !value)}
                      startIcon={<Inventory2OutlinedIcon />}
                      endIcon={(
                        <ExpandMoreRoundedIcon
                          sx={{ transform: archiveOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }}
                        />
                      )}
                      sx={{
                        minHeight: 40,
                        px: 1.25,
                        borderRadius: 2.25,
                        justifyContent: 'flex-start',
                        color: '#526579',
                        background: '#F8FAFC',
                        textTransform: 'none',
                        fontWeight: 800,
                        '& .MuiButton-endIcon': { ml: 'auto' },
                      }}
                    >
                      Заменённые приборы · {archivedDevices.length}
                    </Button>
                    {archiveOpen ? (
                      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                        {archivedDevices.map((device) => (
                          <Box
                            id={`apartment-device-${device.id}`}
                            key={device.id}
                            sx={{ scrollMarginTop: 120 }}
                          >
                            <ArchivedDeviceCard
                              device={device}
                              onOpenDocuments={setDocumentsDevice}
                            />
                          </Box>
                        ))}
                      </Stack>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 1.5, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.4, py: 1.1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 850, color: '#1F3550' }}>
                  {`Заявки по ${labels.apartment.toLowerCase()}`}
                </Typography>
                <Button
                  size="small"
                  component={RouterLink}
                  to={`/tickets?apartment_id=${apartment.id}`}
                  sx={{ fontWeight: 750 }}
                >
                  Открыть журнал
                </Button>
              </Stack>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Заявка</TableCell>
                      <TableCell sx={tableHeadCellSx}>Статус</TableCell>
                      <TableCell sx={tableHeadCellSx}>Приоритет</TableCell>
                      <TableCell sx={tableHeadCellSx}>Создана</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overview.tickets.length ? overview.tickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        hover
                        component={RouterLink}
                        to={`/tickets/${ticket.id}`}
                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                      >
                        <TableCell sx={{ ...tableCellSx, color: '#2368B5', fontWeight: 800 }}>
                          №{ticket.number} · {ticket.title}
                        </TableCell>
                        <TableCell sx={tableCellSx}>{statusLabels[ticket.status] || ticket.status}</TableCell>
                        <TableCell sx={tableCellSx}>{priorityLabels[ticket.priority] || ticket.priority}</TableCell>
                        <TableCell sx={tableCellSx}>{formatDate(ticket.created_at)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center', color: '#64748B' }}>
                          {`Заявок по ${labels.apartment.toLowerCase()} нет`}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Box>
      </Stack>
      <DeviceDocumentsDialog
        open={Boolean(documentsDevice)}
        device={documentsDevice}
        onClose={() => setDocumentsDevice(null)}
        onChanged={() => loadOverview({ silent: true })}
      />
      <DeviceLoRaWanDiagnosticsDialog
        open={Boolean(lorawanDiagnosticsDevice)}
        device={lorawanDiagnosticsDevice}
        onClose={() => setLorawanDiagnosticsDevice(null)}
      />
      <DeviceConnectionHubDialog
        open={Boolean(connectionDevice)}
        device={connectionDevice}
        buildingId={overview.building_id}
        onClose={() => setConnectionDevice(null)}
        onChanged={() => loadOverview({ silent: true })}
      />
      <DevicePlacementDialog
        open={Boolean(placementDevice)}
        device={placementDevice}
        buildingId={overview.building_id}
        currentApartmentId={apartment.id}
        currentBuildingName={overview.building_name}
        currentApartmentLabel={`${labels.apartmentShort} ${overview.display_number}`}
        labels={labels}
        onClose={() => setPlacementDevice(null)}
        onChanged={async (message) => {
          setActionSuccess(message);
          await loadOverview({ silent: true });
        }}
      />
      <ReplaceDeviceDialog
        open={Boolean(replacementDevice)}
        device={replacementDevice}
        onClose={() => setReplacementDevice(null)}
        onReplaced={async () => {
          setReplacementDevice(null);
          setActionError('');
          setActionSuccess('Замена сохранена. Старый прибор перенесён в историю.');
          await loadOverview({ silent: true });
        }}
      />
      <AddDeviceDialog
        open={addDeviceOpen}
        onClose={() => setAddDeviceOpen(false)}
        corpsId={overview.corp_id}
        buildingId={overview.building_id}
        fixedApartment={apartment}
        onSuccess={() => loadOverview({ silent: true })}
      />
    </Box>
  );
}
